
import { Button } from "@/components/ui/button";
import { Link, useNavigate } from "react-router-dom";
import { CreditCard, Menu, X, User, ShoppingCart, Home, LogOut } from "lucide-react";
import { useState, useEffect } from "react";
import { useSessionContext } from "@supabase/auth-helpers-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { session } = useSessionContext();
  const [userRole, setUserRole] = useState<string | null>(null);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    const fetchUserRole = async () => {
      if (!session?.user?.id) {
        setUserRole(null);
        return;
      }

      const { data, error } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', session.user.id)
        .single();

      if (!error && data) {
        setUserRole(data.role);
      }
    };

    fetchUserRole();
  }, [session]);

  const goToDashboard = () => {
    if (!userRole) {
      toast({
        title: "Authentication required",
        description: "Please sign in to access your dashboard",
        variant: "destructive",
      });
      navigate('/signin');
      return;
    }

    switch (userRole) {
      case 'borrower':
        navigate('/borrower/dashboard');
        break;
      case 'lender':
        navigate('/lender/dashboard');
        break;
      case 'vendor':
        navigate('/vendor/dashboard');
        break;
      case 'admin':
        navigate('/admin/dashboard');
        break;
      default:
        toast({
          title: "Access denied",
          description: "You don't have permission to access any dashboard",
          variant: "destructive",
        });
        navigate('/signin');
    }
  };

  const handleSignOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error('Error signing out:', error);
        toast({
          title: "Error",
          description: "Failed to sign out. Please try again.",
          variant: "destructive",
        });
        return;
      }
      
      toast({
        title: "Success",
        description: "You have been signed out successfully.",
      });
      navigate('/');
    } catch (error) {
      console.error('Unexpected error during sign out:', error);
      toast({
        title: "Error",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleNavigation = (path: string) => {
    setIsMenuOpen(false); // Close mobile menu if open
    navigate(path);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-sm border-b">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-14 md:h-16">
          <Link to="/" className="text-lg md:text-xl font-bold text-primary flex items-center gap-2">
            <CreditCard className="h-5 w-5 md:h-6 md:w-6" />
            Obotan
          </Link>
          
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>

          <nav className={`${isMenuOpen ? 'absolute top-full left-0 right-0 bg-white border-b shadow-lg' : 'hidden'} md:flex md:items-center md:space-x-4 md:static md:shadow-none md:border-none`}>
            <div className={`flex flex-col md:flex-row gap-2 p-4 md:p-0 ${isMenuOpen ? 'animate-in slide-in-from-top-4' : ''}`}>
              <Button 
                variant="ghost" 
                className="justify-start text-sm text-muted-foreground hover:text-primary px-2 py-1.5 rounded-md transition-colors"
                onClick={() => handleNavigation('/borrowers')}
              >
                Borrowers
              </Button>
              <Button 
                variant="ghost" 
                className="justify-start text-sm text-muted-foreground hover:text-primary px-2 py-1.5 rounded-md transition-colors"
                onClick={() => handleNavigation('/shop')}
              >
                Shop
              </Button>
              <Button 
                variant="ghost" 
                className="justify-start text-sm text-muted-foreground hover:text-primary px-2 py-1.5 rounded-md transition-colors"
                onClick={() => handleNavigation('/real-estate')}
              >
                Real Estate
              </Button>
              <Button 
                variant="ghost" 
                className="justify-start text-sm text-muted-foreground hover:text-primary px-2 py-1.5 rounded-md transition-colors"
                onClick={() => handleNavigation('/vendors')}
              >
                Vendors
              </Button>
              <Button 
                variant="ghost" 
                className="justify-start text-sm text-muted-foreground hover:text-primary px-2 py-1.5 rounded-md transition-colors"
                onClick={() => handleNavigation('/lenders')}
              >
                Lenders
              </Button>
              <Button 
                variant="ghost" 
                className="justify-start text-sm text-muted-foreground hover:text-primary px-2 py-1.5 rounded-md transition-colors"
                onClick={() => handleNavigation('/about')}
              >
                About Us
              </Button>
              
              {!session ? (
                <>
                  <Button 
                    variant="ghost" 
                    className="justify-start text-sm text-muted-foreground hover:text-primary px-2 py-1.5 rounded-md transition-colors"
                    onClick={() => handleNavigation('/signin')}
                  >
                    Sign In
                  </Button>
                  <Button onClick={() => handleNavigation('/register')} className="w-full md:w-auto">
                    Get Started
                  </Button>
                </>
              ) : (
                <div className="flex flex-col md:flex-row gap-2">
                  {userRole === 'borrower' && (
                    <Button 
                      variant="ghost" 
                      className="justify-start text-sm text-muted-foreground hover:text-primary px-2 py-1.5 rounded-md transition-colors flex items-center"
                      onClick={() => handleNavigation('/borrower/properties')}
                    >
                      <Home className="h-4 w-4 mr-1" />
                      My Properties
                    </Button>
                  )}
                  <Button onClick={goToDashboard} variant="outline" className="w-full md:w-auto">
                    <User className="mr-2 h-4 w-4" />
                    My Dashboard
                  </Button>
                  
                  {/* Sign Out Button */}
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="ghost" className="justify-start text-sm text-destructive hover:text-destructive/90 px-2 py-1.5 rounded-md transition-colors flex items-center">
                        <LogOut className="h-4 w-4 mr-1" />
                        Sign Out
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Are you sure you want to sign out?</AlertDialogTitle>
                        <AlertDialogDescription>
                          You will need to sign in again to access your account.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction 
                          onClick={handleSignOut}
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                          Sign Out
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              )}
            </div>
          </nav>
          
          {/* Cart icon visible at all times for authenticated users */}
          {session && (
            <Button 
              variant="ghost" 
              size="icon" 
              className="ml-2"
              onClick={() => handleNavigation('/cart')}
            >
              <ShoppingCart className="h-5 w-5" />
            </Button>
          )}
        </div>
      </div>
    </header>
  );
};
